// Pure selector translating an `AuctionSnapshot | undefined` into the
// `{ paused, bidIncrements, coarsePhase, lotExtensionSeconds, lotPhaseInput,
// fresh }` shape every consumer reads, so the derivation doesn't
// drift across call sites.
//
// IsomorphicService per ADR 010 / sharing.md §3 — no I/O, no writes, no auth.
//
// BUG-1299 lifted this out of `apps/bidder-web/src/hooks/auction-snapshot-state.ts`
// into the shared package. It lived app-local on the stated grounds that it
// "pairs with the web-only `useSnapshot` store" — but `useSnapshot` is itself
// exported from `@auctogon/core/realtime`, and the admin clerk console now needs
// the same derivation. A second copy in admin-web would be exactly the cross-app
// duplication sharing.md §1 bans, so the one implementation moved here and both
// apps' hooks consume it.
//
// ADR023 (BL-645): the auction snapshot carries the lifecycle DECISION STAMPS,
// not a `status` label. The selector derives a coarse auction phase from those
// stamps via `deriveAuctionPhaseCoarse` — single realtime source, every input
// from the one snapshot — so a consumer's status-dependent UI re-derives on the
// client's tick without a republish.

import {
    deriveAuctionPhaseCoarse,
    type AuctionCoarsePhaseKind,
    type LotPhaseAuctionInput,
} from '../lifecycle';

import type { AuctionSnapshot } from '../../mappers/realtime/auction-snapshot';

/**
 * Effective auction state derived from the realtime snapshot, with a single
 * freshness signal. `fresh: false` means the catch-up hasn't landed yet —
 * consumers get "no realtime claim has been made" defaults so first paint
 * doesn't fabricate state.
 *
 * `coarsePhase` is the auction's lifecycle phase derived from the snapshot's
 * decision stamps. `null` until the catch-up lands. Per the SACRED
 * "Realtime-Projected Fields Are Realtime-Only on the Client" rule, the
 * snapshot is the exclusive live source for these values on a subscribing
 * surface. (Coarse = post-sale folds into `active`; the auction snapshot
 * doesn't carry its lots, so the all-lots-terminal distinction isn't available
 * client-side — the per-lot snapshots carry the lot-level closed state.)
 */
export interface AuctionEffectiveState {
    paused: boolean;
    bidIncrements: unknown | null;
    coarsePhase: AuctionCoarsePhaseKind | null;
    /**
     * BUG-1299: the anti-snipe extension in seconds, or `null` when no snapshot
     * has landed. `null` is NOT the same as `0` — 0 means anti-snipe is off, an
     * operator's real choice. A consumer applying this to an extend action must
     * treat `null` as "don't know yet", never as a duration.
     */
    lotExtensionSeconds: number | null;
    /**
     * BL-2302: the auction half of the lot ladder — `deriveLotPhase`'s second
     * argument — from this ONE snapshot. The single owner of the snapshot →
     * `{ openedAt, preBiddingAt, closingType }` mapping for both apps (the clerk
     * console, the Lot Edit drawer, the bidder's lot phase): three call sites
     * used to spell it inline. `null` until the catch-up lands — never a guessed
     * auction, which would derive every lot as Upcoming.
     */
    lotPhaseInput: LotPhaseAuctionInput | null;
    fresh: boolean;
}

/** Default state used before the snapshot catch-up lands. */
export const DEFAULT_AUCTION_EFFECTIVE_STATE: AuctionEffectiveState = {
    paused: false,
    bidIncrements: null,
    coarsePhase: null,
    lotExtensionSeconds: null,
    lotPhaseInput: null,
    fresh: false,
};

/**
 * Translate an `AuctionSnapshot | undefined` to the effective state every
 * consumer reads. Pure — safe inside `useMemo`.
 *
 * `snapshot === undefined` means the catch-up hasn't landed yet
 * (`SnapshotContext` returns undefined for unknown topics). Defaults favour
 * "no realtime claim has been made" so the UI doesn't render state from absence.
 *
 * @param snapshot The auction's realtime snapshot, or `undefined` pre-catch-up.
 * @returns The {@link AuctionEffectiveState} consumers render from.
 */
export function selectAuctionEffectiveState(
    snapshot: AuctionSnapshot | undefined,
): AuctionEffectiveState {
    if (snapshot === undefined) return DEFAULT_AUCTION_EFFECTIVE_STATE;
    return {
        paused: snapshot.pausedAt !== null,
        bidIncrements: snapshot.bidIncrements,
        coarsePhase: deriveAuctionPhaseCoarse({
            deletedAt: snapshot.deletedAt,
            closedAt: snapshot.closedAt,
            pausedAt: snapshot.pausedAt,
            openedAt: snapshot.openedAt,
            readiedAt: snapshot.readiedAt,
            // BL-1291: the auction's own pre-bidding stamp, from this SAME
            // snapshot — which is what makes the coarse phase single-source.
            preBiddingAt: snapshot.preBiddingAt,
        }),
        // `?? null` not `|| null`: 0 is a real extension value (anti-snipe off)
        // and must survive the read (company-standards.md "0 is a value").
        lotExtensionSeconds: snapshot.lotExtensionSeconds ?? null,
        lotPhaseInput: {
            openedAt: snapshot.openedAt,
            // BL-1291: the auction half of the two-stamp pre-bidding window.
            preBiddingAt: snapshot.preBiddingAt,
            // BUG-909: the real closing type splits `last-call` (manual) from
            // `awaiting-resolution`; an absent one falls to automatic, as the
            // server's lot read defaults it (lot-detail-service).
            closingType: snapshot.closingType === 'manual' ? 'manual' : 'automatic',
        },
        fresh: true,
    };
}
